import React from "react"
import { useForm, Controller } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { Button } from "src/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "src/components/ui/card"
import { Checkbox } from "src/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "src/components/ui/form"
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "src/components/ui/accordion"
import UserAdminService from "./UserAdminService"
import { availableGroups } from "./ExternalUsers"
import { ExternalUser, ExternalUserUpdate, ProjectAccess, updateExternalUserApi } from "src/services/users"
import { Settings, Shield, ChevronDown, Info } from "lucide-react"

interface EditUserFormData {
  accessPolicies: Record<string, boolean>
  userTypeId: string
  projectAccess: {
    restrictProjects: "no" | "yes" | "portfolios"
    projects: Record<string, boolean>
    portfolios: Record<string, boolean>
    preventDownload: "yes" | "no"
  }
}

interface UserType {
  id: string
  name: string
  defaultGroups: string[]
}

interface EditExternalUserProps {
  user: ExternalUser | null
  onClose: () => void
  projects?: Array<{ code: string; name: string }>
  portfolios?: Array<{ id: string; name: string }>
}

const EditExternalUser: React.FC<EditExternalUserProps> = ({ user, onClose, projects = [], portfolios = [] }) => {
  const form = useForm<EditUserFormData>({
    defaultValues: {
      accessPolicies: {},
      userTypeId: user?.userType.id || "",
      projectAccess: {
        restrictProjects: "no",
        projects: {},
        portfolios: {},
        preventDownload: "no",
      },
    },
  })
  const [userTypes, setUserTypes] = React.useState<UserType[]>([])
  const queryClient = useQueryClient()
  
  const restrictProjects = form.watch("projectAccess.restrictProjects")

  React.useEffect(() => {
    async function getUserTypes() {
      const fetchedUserTypes = (await UserAdminService.getUserTypes()) as {
        userTypes: UserType[]
      }
      setUserTypes(fetchedUserTypes.userTypes)
    }
    getUserTypes()
  }, [])

  // Update accessPolicies when userTypeId changes
  React.useEffect(() => {
    if (user && userTypes.length > 0) {
      const selectedUserType = userTypes.find((type) => type.id === form.getValues("userTypeId"))
      if (selectedUserType) {
        const defaultPolicies = availableGroups.reduce(
          (acc, group) => ({
            ...acc,
            [group]: selectedUserType.defaultGroups.includes(group),
          }),
          {},
        )
        form.setValue("accessPolicies", defaultPolicies)
      }
    }
  }, [form.watch("userTypeId"), user, userTypes])

  React.useEffect(() => {
    if (user && userTypes.length > 0) {
      // Initialize form with user data
      const accessPolicies = availableGroups.reduce(
        (acc, group) => ({
          ...acc,
          [group]: user.accessPolicies.includes(group),
        }),
        {},
      )

      const projectAccess = user.projectAccess || {
        restrictProjects: "no",
        projects: [],
        portfolios: [],
        preventDownload: "no",
      }

      const projectsMap = projects.reduce(
        (acc, project) => ({
          ...acc,
          [project.code]: projectAccess.projects?.includes(project.code) || false,
        }),
        {},
      )

      const portfoliosMap = portfolios.reduce(
        (acc, portfolio) => ({
          ...acc,
          [portfolio.id]: projectAccess.portfolios?.includes(portfolio.id) || false,
        }),
        {},
      )

      form.reset({
        accessPolicies,
        userTypeId: user.userType.id,
        projectAccess: {
          restrictProjects: projectAccess.restrictProjects || "no",
          projects: projectsMap,
          portfolios: portfoliosMap,
          preventDownload: projectAccess.preventDownload || "no",
        },
      })
    }
  }, [user, userTypes, projects, portfolios])

  const mutation = useMutation(updateExternalUserApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(["externalUsers"])
      toast.success("Successfully updated external user!")
      onClose()
    },
    onError: (error) => {
      toast.error("Something went wrong while updating the user!")
      console.error("Error updating external user:", error)
    },
  })

  const onSubmit = (data: EditUserFormData) => {
    if (!user) return

    const selectedPolicies = Object.entries(data.accessPolicies)
      .filter(([_, isChecked]) => isChecked)
      .map(([policy]) => policy)

    const selectedProjects = Object.entries(data.projectAccess.projects)
      .filter(([_, isChecked]) => isChecked)
      .map(([project]) => project)

    const selectedPortfolios = Object.entries(data.projectAccess.portfolios)
      .filter(([_, isChecked]) => isChecked)
      .map(([portfolio]) => portfolio)

    const projectAccess: ProjectAccess = {
      restrictProjects: data.projectAccess.restrictProjects,
      preventDownload: data.projectAccess.preventDownload,
    }

    if (data.projectAccess.restrictProjects === "yes") {
      projectAccess.projects = selectedProjects
    } else if (data.projectAccess.restrictProjects === "portfolios") {
      projectAccess.portfolios = selectedPortfolios
    }

    const payload: ExternalUserUpdate = {
      userId: user.userId,
      updates: {
        accessPolicies: selectedPolicies,
        userType: {
          id: data.userTypeId,
          name: userTypes.find((ut) => ut.id === data.userTypeId)?.name || "",
        },
        projectAccess: projectAccess,
      },
    }

    mutation.mutate(payload)
  }

  if (!user) return null

  return (
    <Card className="tw-bg-background tw-text-foreground tw-w-full tw-bg-white tw-shadow-lg tw-rounded-lg">
      <CardHeader className="tw-border-b tw-border-gray-200">
        <CardTitle className="tw-text-xl tw-font-semibold">Edit External User</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="tw-space-y-6">
          <CardContent className="tw-space-y-6 tw-pt-6">
            {/* Email Address Field */}
            <FormItem className="tw-space-y-2">
              <FormLabel className="tw-text-sm tw-font-medium">Email</FormLabel>
              <Input value={user.email} disabled className="tw-bg-gray-50" />
            </FormItem>

            {/* User Type Selection */}
            <FormField
              control={form.control}
              name="userTypeId"
              rules={{ required: "User Type is required" }}
              render={({ field }) => (
                <FormItem className="tw-space-y-2">
                  <FormLabel className="tw-text-sm tw-font-medium">User Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="tw-w-full">
                        <SelectValue placeholder="Select a user type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userTypes.map((userType) => (
                        <SelectItem key={userType.id} value={userType.id}>
                          {userType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="tw-text-red-500 tw-text-sm" />
                </FormItem>
              )}
            />

            {/* Access Policies Accordion */}
            <div className="tw-mt-4">
              <Accordion type="single" collapsible defaultValue="access-policies">
                <AccordionItem value="access-policies">
                  <AccordionTrigger className="tw-flex tw-items-center">
                    <div className="tw-flex">
                      <Settings className="tw-w-5 tw-h-5" />
                      <span className="tw-ml-2">Access Policies</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mt-2">
                      {availableGroups.map((group) => (
                        <FormField
                          key={group}
                          control={form.control}
                          name={`accessPolicies.${group}`}
                          render={({ field }) => (
                            <FormItem className="tw-flex tw-items-center">
                              <FormControl>
                                <Checkbox
                                  id={`accessPolicies.${group}`}
                                  checked={field.value || false}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <Label htmlFor={`accessPolicies.${group}`} className="tw-ml-2 tw-capitalize">
                                {group.replace(/-/g, " ")}
                              </Label>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Project/Portfolio Access Accordion */}
                <AccordionItem value="project-access">
                  <AccordionTrigger className="tw-flex tw-items-center">
                    <div className="tw-flex">
                      <Shield className="tw-w-5 tw-h-5" />
                      <span className="tw-ml-2">Project/Portfolio Access</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="tw-space-y-4">
                      {/* Restrict Projects Selection */}
                      <FormField
                        control={form.control}
                        name="projectAccess.restrictProjects"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Access Mode</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="no">Unrestricted Access</SelectItem>
                                <SelectItem value="yes">Restrict to Specific Projects</SelectItem>
                                <SelectItem value="portfolios">Restrict to Specific Portfolios</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />

                      {/* Projects Selection */}
                      {restrictProjects === "yes" && (
                        <div>
                          <Label>Select Projects</Label>
                          {!projects || projects.length === 0 ? (
                            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-3 tw-mt-2">
                              <p className="tw-text-yellow-800 tw-text-sm">No projects available. Please ensure projects are loaded in the system.</p>
                            </div>
                          ) : (
                          <div className="tw-max-h-48 tw-overflow-y-auto tw-border tw-rounded tw-p-2 tw-mt-1">
                            {projects.map((project) => (
                              <FormField
                                key={project.code}
                                control={form.control}
                                name={`projectAccess.projects.${project.code}`}
                                render={({ field }) => (
                                  <div className="tw-flex tw-items-center tw-py-1">
                                    <Checkbox
                                      id={`project-${project.code}`}
                                      checked={field.value || false}
                                      onCheckedChange={field.onChange}
                                    />
                                    <Label htmlFor={`project-${project.code}`} className="tw-ml-2 tw-cursor-pointer">
                                      {project.name} ({project.code})
                                    </Label>
                                  </div>
                                )}
                              />
                            ))}
                          </div>
                          )}
                        </div>
                      )}

                      {/* Portfolios Selection */}
                      {restrictProjects === "portfolios" && (
                        <div>
                          <Label>Select Portfolios</Label>
                          {!portfolios || portfolios.length === 0 ? (
                            <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-p-3 tw-mt-2">
                              <p className="tw-text-yellow-800 tw-text-sm">No portfolios available. Please ensure portfolios are configured in the system.</p>
                            </div>
                          ) : (
                          <div className="tw-max-h-48 tw-overflow-y-auto tw-border tw-rounded tw-p-2 tw-mt-1">
                            {portfolios.map((portfolio) => (
                              <FormField
                                key={portfolio.id}
                                control={form.control}
                                name={`projectAccess.portfolios.${portfolio.id}`}
                                render={({ field }) => (
                                  <div className="tw-flex tw-items-center tw-py-1">
                                    <Checkbox
                                      id={`portfolio-${portfolio.id}`}
                                      checked={field.value || false}
                                      onCheckedChange={field.onChange}
                                    />
                                    <Label htmlFor={`portfolio-${portfolio.id}`} className="tw-ml-2 tw-cursor-pointer">
                                      {portfolio.name}
                                    </Label>
                                  </div>
                                )}
                              />
                            ))}
                          </div>
                          )}
                        </div>
                      )}

                      {/* Prevent Download Option */}
                      <FormField
                        control={form.control}
                        name="projectAccess.preventDownload"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Download Permission</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="no">Allow Downloads</SelectItem>
                                <SelectItem value="yes">Prevent Downloads</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Info Message */}
            <div className="tw-mt-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
              <div className="tw-flex tw-items-start">
                <Info className="tw-w-5 tw-h-5 tw-text-blue-600 tw-mt-0.5 tw-mr-3 tw-flex-shrink-0" />
                <div className="tw-text-sm tw-text-blue-800">
                  <p className="tw-font-medium tw-mb-1">Important Note:</p>
                  <p>Project and portfolio restrictions will take effect when the user:</p>
                  <ul className="tw-list-disc tw-list-inside tw-mt-1 tw-ml-2">
                    <li>Logs out and logs back in, or</li>
                    <li>Switches to another organization and switches back</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="tw-flex tw-justify-between tw-border-t tw-border-gray-200 tw-pt-6">
            <Button variant="secondary" onClick={onClose} type="button" className="tw-bg-gray-300 hover:tw-bg-gray-400">
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isLoading} className="tw-bg-blue-600 hover:tw-bg-blue-700">
              {mutation.isLoading ? "Updating..." : "Update"}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  )
}

export default EditExternalUser
